// The scan step. A handheld scan (HID key events) or a typed serial plus Enter is checked
// against the drone lookup, then joins the list newest first. A refusal says why in the line
// under the field and adds nothing. An archived drone is added with its flag, because running
// the batch brings it back.

import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../apiHelpers';
import type { ScanResult } from '../../components/BarcodeScanner/types';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import useScannerInput from '../hooks/useScannerInput';
import { ScanFrameIcon, WarningIcon } from '../icons';
import { buzz } from '../shell';
import { focusForScan, ScanField } from '../ui';
import type { MarkedDrone } from './types';

const BarcodeScannerModal = lazy(() => import('../../components/BarcodeScanner/BarcodeScannerModal'));

export const BATCH_CAP = 1000;
const SERIAL_RE = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i;

// Module scope so the hook's listeners keep a stable identity.
const isSerial = (val: string) => SERIAL_RE.test(val);

interface Props {
  drones: MarkedDrone[];
  onAdd: (drone: MarkedDrone) => void;
  onRemove: (serial: string) => void;
  onNext: () => void;
  /** The Next key's words when the action is already chosen (a shortcut opened Marker on one). */
  nextLabel?: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const MarkerScan: React.FC<Props> = ({ drones, onAdd, onRemove, onNext, nextLabel }) => {
  const api = useApi();
  const scanRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<string | null>(null);
  const [looking, setLooking] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);

  // Read from refs so a duplicate check never lives inside a state updater or a stale closure.
  const dronesRef = useRef(drones);
  dronesRef.current = drones;
  const pending = useRef(new Set<string>());
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const say = (text: string | null, refused = false) => {
    if (!alive.current) return;
    setNote(text);
    if (refused) buzz('reject');
  };

  const add = useCallback(async (raw: string) => {
    const serial = raw.trim().toUpperCase();
    if (!serial) return;
    if (!SERIAL_RE.test(serial)) return say(`${serial} is not a drone serial.`, true);
    if (dronesRef.current.some((d) => d.serial === serial) || pending.current.has(serial)) {
      return say(`${serial} is already on the list.`, true);
    }
    if (dronesRef.current.length + pending.current.size >= BATCH_CAP) {
      return say(`The list holds ${BATCH_CAP} drones. Mark these, then start another list.`, true);
    }
    say(null);
    pending.current.add(serial);
    setLooking((n) => n + 1);
    try {
      const res: any = await api.get(`/scanner/drone/${encodeURIComponent(serial)}`);
      if (alive.current) onAdd({ serial, archived: res?.drone?.archived === true });
    } catch (e: any) {
      const status = e?.status ?? e?.statusCode;
      if (status === 404 || /\b404\b/.test(e?.message || '')) say(`${serial} was not found. Check the sticker.`, true);
      else say(`${serial} could not be checked: ${e?.message || 'no answer'}. Scan it again.`, true);
    } finally {
      pending.current.delete(serial);
      if (alive.current) setLooking((n) => n - 1);
    }
  }, [onAdd]);

  useScannerInput({ onScan: add, surface: 'marker', minLength: 19, validator: isSerial, disabled: cameraOpen });

  const archived = drones.filter((d) => d.archived).length;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
      <div className="shrink-0 px-4 pb-3">
        <div className="mb-3 text-center" aria-live="polite">
          <p className="flex items-baseline justify-center gap-2">
            <span className="font-mono text-3xl font-semibold tabular-nums text-on-surface">{drones.length}</span>
            <span className="text-base text-on-surface-variant">{drones.length === 1 ? 'drone' : 'drones'} on the list</span>
          </p>
          {archived > 0 && (
            <p className="mt-1 text-sm text-amber-200">
              {plural(archived, 'archived drone', 'archived drones')} will be brought back
            </p>
          )}
        </div>
        <ScanField inputRef={scanRef} label="Scan each drone to mark" busy={looking > 0} onSubmit={add} />
        <p role="status" className="mt-1 min-h-[1.25rem] text-center text-sm text-on-surface-variant">{note ?? ''}</p>
        <button
          type="button"
          onClick={() => setCameraOpen(true)}
          className={`${BTN_TONAL} mt-1 min-h-[2.75rem] w-full justify-center`}
        >
          <ScanFrameIcon className="h-5 w-5" />
          Scan with the camera
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
        {drones.length === 0 ? (
          <p className="py-6 text-center text-base text-on-surface-variant">
            Scan the first drone to start the list.
          </p>
        ) : (
          <ul aria-label="Drones to mark" className="flex flex-col gap-1.5">
            {[...drones].reverse().map((d) => (
              <li
                key={d.serial}
                className={`flex min-h-[2.75rem] items-center gap-2 rounded-lg py-0.5 pl-3 pr-1 motion-safe:animate-rowLand
                  ${d.archived ? 'bg-amber-900/30 text-on-surface' : 'bg-surface-container text-on-surface'}`}
              >
                {d.archived && <WarningIcon className="h-5 w-5 shrink-0 text-amber-200" />}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-mono text-base">{d.serial}</span>
                  {d.archived && <span className="text-xs font-medium text-amber-200">Archived: it will be brought back.</span>}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onRemove(d.serial);
                    focusForScan(scanRef);
                  }}
                  aria-label={`Remove ${d.serial}`}
                  title={`Take ${d.serial} off the list`}
                  className={`inline-flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface ${FOCUS} ${MOTION}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="shrink-0 bg-surface px-4 pb-4 pt-2">
        <button
          type="button"
          onClick={onNext}
          disabled={drones.length === 0 || looking > 0}
          className={`min-h-[3.25rem] w-full rounded-full bg-primary px-5 text-base font-bold text-on-primary np-state np-state-on-primary disabled:opacity-40 ${FOCUS} ${MOTION}`}
        >
          {nextLabel ?? (drones.length > 0 ? `Choose what to do with ${drones.length}` : 'Choose what to do')}
        </button>
      </div>

      {cameraOpen && (
        <Suspense
          fallback={
            <div className="scrim z-[100] flex items-center justify-center">
              <div className="text-sm text-on-surface-variant">Loading camera…</div>
            </div>
          }
        >
          <BarcodeScannerModal
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onScan={(result: ScanResult) => {
              setCameraOpen(false);
              add(result.text);
            }}
            title="Scan a drone serial"
          />
        </Suspense>
      )}
    </div>
  );
};

export default MarkerScan;
